import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as echarts from "echarts/core";
import { TreemapChart } from "echarts/charts";
import { TooltipComponent } from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import type { EChartsOption } from "echarts";

import type { HeatmapNode } from "../types";

echarts.use([TreemapChart, TooltipComponent, CanvasRenderer]);

export type MarketTab = "Overview";
export type MapFilter = string;

export type FinanceCollection = {
  asset_class: string;
  id: string;
  kind: string;
  label: string;
};

async function fetchJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(url, {
    signal,
    headers: { Accept: "application/json" },
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Request failed (${res.status})${text ? `: ${text}` : ""}`);
  }

  return (await res.json()) as T;
}

function clamp(x: number, min: number, max: number) {
  return Math.max(min, Math.min(max, x));
}

function toHex(n: number) {
  return Math.round(n).toString(16).padStart(2, "0");
}

function rgbToHex(r: number, g: number, b: number) {
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

function estimateTabWidth(label: string) {
  return clamp(label.length * 7 + 24, 64, 160);
}

function normalizeHeatmapPayload(payload: any): HeatmapNode | null {
  if (!payload) return null;
  const root = (payload as any)?.data ?? payload;
  if (!root || typeof root !== "object") return null;
  return root as HeatmapNode;
}

/**
 * Finviz-like diverging heat color.
 * - Neutral: dark gray
 * - Positive: green intensity increases
 * - Negative: red intensity increases
 */
function chgToColor(chg?: number | null, isDark = true) {
  // Palette chosen for a dark UI. Adjust as needed.
  const neutral = isDark ? [48, 54, 64] : [226, 232, 240]; // dark gray vs slate-200
  const pos = [0, 200, 90]; // green
  const neg = [230, 70, 70]; // red

  if (chg == null || Number.isNaN(chg))
    return rgbToHex(neutral[0], neutral[1], neutral[2]);

  // Clamp to typical daily range; you can make this dynamic from the dataset if desired.
  const maxAbs = 5; // percent
  const v = clamp(chg, -maxAbs, maxAbs) / maxAbs;

  if (Math.abs(v) < 1e-6) return rgbToHex(neutral[0], neutral[1], neutral[2]);

  const t = Math.abs(v);
  const target = v > 0 ? pos : neg;
  const r = lerp(neutral[0], target[0], t);
  const g = lerp(neutral[1], target[1], t);
  const b = lerp(neutral[2], target[2], t);
  return rgbToHex(r, g, b);
}

function formatPct(x?: number | null) {
  if (x == null || Number.isNaN(x)) return "—";
  const sign = x > 0 ? "+" : "";
  return `${sign}${x.toFixed(2)}%`;
}

function inferredValue(node: HeatmapNode): number {
  if (typeof node.value === "number" && node.value > 0) return node.value;
  const kids = node.children ?? [];
  if (kids.length === 0) return 1;
  return kids.reduce((sum, c) => sum + inferredValue(c), 0);
}

function transformForTreemap(node: HeatmapNode, isDark: boolean): any {
  const value = inferredValue(node);
  const chg = (node as any).changePct ?? (node as any).chg ?? null;

  const base: any = {
    name: node.name,
    value,
    chg,
    itemStyle: {
      color: chgToColor(chg, isDark),
      borderColor: isDark ? "#0f141a" : "#ffffff",
      borderWidth: 1,
      gapWidth: 1,
    },
  };

  if (node.children && node.children.length > 0) {
    base.children = node.children.map((c) => transformForTreemap(c, isDark));
  }

  return base;
}

function Chevron({ direction }: { direction: "up" | "down" }) {
  const d = direction === "down" ? "M5 7.5l5 5 5-5" : "M5 12.5l5-5 5 5";

  return (
    <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" aria-hidden="true">
      <path
        d={d}
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export type MarketHeatmapCardProps = {
  className?: string;

  autoFetch?: boolean;

  collections?: FinanceCollection[] | null;

  selectedCollectionId?: string | null;
  setSelectedCollectionId?: (id: string) => void;

  initialCollectionId?: string;

  heatmap?: HeatmapNode | null;

  tab?: MarketTab;
  setTab?: (t: MarketTab) => void;
  mapFilter?: MapFilter;
  setMapFilter?: (v: MapFilter) => void;
  theme?: "light" | "dark";
};

export function MarketHeatmapCard({
  autoFetch = true,
  collections: collectionsProp,
  selectedCollectionId: selectedCollectionIdProp,
  setSelectedCollectionId: setSelectedCollectionIdProp,
  initialCollectionId,
  heatmap: heatmapProp,
  tab = "Overview",
  setTab: _setTab,
  mapFilter,
  setMapFilter,
  theme = "dark",
  className,
}: MarketHeatmapCardProps) {
  const isDark = theme === "dark";
  const chartDomRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<echarts.EChartsType | null>(null);

  const tabButtonBaseClass =
    "whitespace-nowrap rounded-full px-3 py-1 text-[11px] font-semibold tracking-wide transition-colors max-w-[160px] truncate";

  const [tabsExpanded, setTabsExpanded] = useState(false);
  const tabsListRef = useRef<HTMLDivElement | null>(null);
  const tabsMeasureRef = useRef<HTMLDivElement | null>(null);
  const [tabsListWidth, setTabsListWidth] = useState(0);
  const [tabWidths, setTabWidths] = useState<Record<string, number>>({});

  const [collectionsState, setCollectionsState] = useState<FinanceCollection[]>(
    []
  );
  const [collectionsLoading, setCollectionsLoading] = useState(false);
  const [collectionsError, setCollectionsError] = useState<string | null>(null);
  const [collectionsReloadKey, setCollectionsReloadKey] = useState(0);

  const [selectedCollectionIdState, setSelectedCollectionIdState] = useState<
    string | null
  >(initialCollectionId ?? null);

  const [heatmapState, setHeatmapState] = useState<HeatmapNode | null>(
    () => normalizeHeatmapPayload(heatmapProp) ?? null
  );
  const [mapLoading, setMapLoading] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const [mapReloadKey, setMapReloadKey] = useState(0);

  const collections = collectionsProp ?? collectionsState;

  const isSelectionControlled =
    selectedCollectionIdProp !== undefined &&
    typeof setSelectedCollectionIdProp === "function";
  const selectedCollectionId = isSelectionControlled
    ? selectedCollectionIdProp
    : selectedCollectionIdState;

  const heatmap = autoFetch
    ? heatmapState
    : normalizeHeatmapPayload(heatmapProp) ?? null;

  const selectedCollection = useMemo(() => {
    if (!selectedCollectionId) return null;
    return collections.find((c) => c.id === selectedCollectionId) ?? null;
  }, [collections, selectedCollectionId]);

  const selectedCollectionLabel = selectedCollection?.label ?? mapFilter ?? "—";

  const setSelectedCollectionId = useCallback(
    (id: string) => {
      setSelectedCollectionIdProp?.(id);
      setSelectedCollectionIdState(id);

      const c = collections.find((x) => x.id === id);
      if (c) setMapFilter?.(c.label);

      // UX: after choosing in expanded mode, collapse back.
      setTabsExpanded(false);
    },
    [collections, setMapFilter, setSelectedCollectionIdProp]
  );

  const reloadCollections = useCallback(
    () => setCollectionsReloadKey((k) => k + 1),
    []
  );
  const reloadMap = useCallback(() => setMapReloadKey((k) => k + 1), []);

  useLayoutEffect(() => {
    if (!tabsListRef.current) return;

    const el = tabsListRef.current;
    const update = () => {
      const w = Math.floor(el.getBoundingClientRect().width);
      if (Number.isFinite(w)) setTabsListWidth(w);
    };

    update();
    const ro = new ResizeObserver(() => update());
    ro.observe(el);

    return () => ro.disconnect();
  }, []);

  useLayoutEffect(() => {
    if (!tabsMeasureRef.current) return;

    const els = Array.from(
      tabsMeasureRef.current.querySelectorAll<HTMLElement>("[data-col-id]")
    );

    const next: Record<string, number> = {};
    for (const el of els) {
      const id = el.dataset.colId;
      if (!id) continue;
      next[id] = Math.ceil(el.getBoundingClientRect().width);
    }

    setTabWidths(next);
  }, [collections]);

  const displayedCollections = useMemo(() => {
    if (tabsExpanded) return collections;
    if (collections.length === 0) return collections;

    const gapPx = 8; // gap-2
    const max = tabsListWidth;

    // If we have no width yet, best-effort show a small prefix.
    if (!max || max <= 0) {
      return collections.slice(0, Math.min(collections.length, 6));
    }

    let used = 0;
    const visible: FinanceCollection[] = [];

    for (const c of collections) {
      const w = tabWidths[c.id] ?? estimateTabWidth(c.label);
      const nextUsed = visible.length === 0 ? w : used + gapPx + w;

      if (nextUsed <= max) {
        visible.push(c);
        used = nextUsed;
      } else {
        break;
      }
    }

    if (
      selectedCollectionId &&
      !visible.some((v) => v.id === selectedCollectionId)
    ) {
      const active = collections.find((c) => c.id === selectedCollectionId);
      if (active) {
        if (visible.length === 0) {
          visible.push(active);
        } else {
          visible[visible.length - 1] = active;
        }
      }
    }

    const out: FinanceCollection[] = [];
    const seen = new Set<string>();
    for (const c of visible) {
      if (seen.has(c.id)) continue;
      seen.add(c.id);
      out.push(c);
    }

    return out;
  }, [
    collections,
    selectedCollectionId,
    tabWidths,
    tabsExpanded,
    tabsListWidth,
  ]);

  const hiddenCount = useMemo(() => {
    if (tabsExpanded) return 0;
    return Math.max(0, collections.length - displayedCollections.length);
  }, [collections.length, displayedCollections.length, tabsExpanded]);

  useEffect(() => {
    if (!autoFetch) return;
    if (collectionsProp !== undefined && collectionsProp !== null) return;

    const ac = new AbortController();
    let alive = true;

    setCollectionsLoading(true);
    setCollectionsError(null);

    fetchJson<FinanceCollection[]>(
      "/api/v1/finance/collections?context=map",
      ac.signal
    )
      .then((data) => {
        if (!alive) return;
        setCollectionsState(Array.isArray(data) ? data : []);
      })
      .catch((err: any) => {
        if (!alive) return;
        if (err?.name === "AbortError") return;
        setCollectionsError(err?.message ?? "Failed to load collections.");
      })
      .finally(() => {
        if (!alive) return;
        setCollectionsLoading(false);
      });

    return () => {
      alive = false;
      ac.abort();
    };
  }, [autoFetch, collectionsProp, collectionsReloadKey]);

  useEffect(() => {
    if (collections.length === 0) return;

    const firstId = collections[0].id;

    if (isSelectionControlled) {
      if (!selectedCollectionIdProp) {
        setSelectedCollectionIdProp?.(firstId);
      }
      return;
    }

    const hasSelection =
      !!selectedCollectionIdState &&
      collections.some((c) => c.id === selectedCollectionIdState);

    if (!hasSelection) {
      setSelectedCollectionIdState(firstId);
    }
  }, [
    collections,
    isSelectionControlled,
    selectedCollectionIdProp,
    selectedCollectionIdState,
    setSelectedCollectionIdProp,
  ]);

  useEffect(() => {
    if (!autoFetch) return;
    if (!selectedCollectionId) return;

    const ac = new AbortController();
    let alive = true;

    setMapLoading(true);
    setMapError(null);
    setHeatmapState(null);

    const url = `/api/v1/finance/map?collection_id=${encodeURIComponent(
      selectedCollectionId
    )}`;

    fetchJson<any>(url, ac.signal)
      .then((payload) => {
        if (!alive) return;
        setHeatmapState(normalizeHeatmapPayload(payload) ?? null);
      })
      .catch((err: any) => {
        if (!alive) return;
        if (err?.name === "AbortError") return;
        setMapError(err?.message ?? "Failed to load heatmap.");
      })
      .finally(() => {
        if (!alive) return;
        setMapLoading(false);
      });

    return () => {
      alive = false;
      ac.abort();
    };
  }, [autoFetch, mapReloadKey, selectedCollectionId]);

  // Init chart once
  useEffect(() => {
    if (!chartDomRef.current) return;

    const dom = chartDomRef.current;
    const instance = echarts.init(dom, undefined, { renderer: "canvas" });
    chartRef.current = instance;

    const ro = new ResizeObserver(() => instance.resize());
    ro.observe(dom);

    return () => {
      ro.disconnect();
      instance.dispose();
      chartRef.current = null;
    };
  }, []);

  const option: EChartsOption | null = useMemo(() => {
    if (!heatmap) return null;

    const opt: EChartsOption = {
      backgroundColor: "transparent",
      animation: false,
      tooltip: {
        trigger: "item",
        confine: true,
        backgroundColor: isDark
          ? "rgba(2, 6, 23, 0.92)"
          : "rgba(255, 255, 255, 0.96)",
        borderColor: isDark ? "transparent" : "#e2e8f0",
        borderWidth: isDark ? 0 : 1,
        padding: 10,
        textStyle: { color: isDark ? "#e5e7eb" : "#0f172a", fontSize: 12 },
        extraCssText: isDark
          ? ""
          : "box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);",
        formatter: (params: any) => {
          const path = (params?.treePathInfo ?? [])
            .map((p: any) => p?.name)
            .filter(Boolean)
            .join(" / ");

          const chg = params?.data?.chg;
          const val = params?.data?.value;

          return `
            <div style="font-size:12px;">
              <div style="margin-bottom:6px; font-weight:600;">${
                path || params?.name || ""
              }</div>
              <div style="display:flex; justify-content:space-between; gap:12px;">
                <div style="opacity:0.75;">Change</div>
                <div style="font-weight:600; font-variant-numeric: tabular-nums;">${formatPct(
                  chg
                )}</div>
              </div>
              <div style="display:flex; justify-content:space-between; gap:12px;">
                <div style="opacity:0.75;">Weight</div>
                <div style="font-weight:600; font-variant-numeric: tabular-nums;">${
                  typeof val === "number"
                    ? Math.round(val).toLocaleString()
                    : "—"
                }</div>
              </div>
            </div>
          `;
        },
      },
      series: [
        {
          type: "treemap",
          data: [transformForTreemap(heatmap, isDark)],
          roam: true,
          nodeClick: false,
          breadcrumb: { show: false },
          upperLabel: {
            show: true,
            height: 18,
            color: isDark ? "rgba(226, 232, 240, 0.85)" : "#334155",
            fontSize: 12,
            overflow: "truncate",
          },
          label: {
            show: true,
            color: isDark ? "#ffffff" : "#0f172a",
            fontSize: 12,
            overflow: "truncate",
            formatter: (p: any) => {
              const isLeaf = !p?.data?.children || p.data.children.length === 0;
              if (!isLeaf) return p?.name ?? "";
              const chg = p?.data?.chg;
              return `${p?.name ?? ""}
${formatPct(chg)}`;
            },
          },
          levels: [
            {
              itemStyle: {
                borderColor: isDark ? "#0b0f14" : "#ffffff",
                borderWidth: 3,
                gapWidth: 2,
              },
              upperLabel: { show: true },
            },
            {
              itemStyle: {
                borderColor: isDark ? "#0b0f14" : "#ffffff",
                borderWidth: 2,
                gapWidth: 1,
              },
              upperLabel: { show: true },
            },
            {
              itemStyle: {
                borderColor: isDark ? "#0f141a" : "#ffffff",
                borderWidth: 1,
                gapWidth: 1,
              },
            },
          ],
          labelLayout: { hideOverlap: true },
        },
      ],
    };

    return opt;
  }, [heatmap, isDark]);

  // Apply option to chart
  useEffect(() => {
    if (!chartRef.current) return;
    if (!option) {
      chartRef.current.clear();
      return;
    }
    chartRef.current.setOption(option, { notMerge: true, lazyUpdate: true });
  }, [option]);

  const showTabsToggle = collections.length > 0;

  return (
    <div
      className={
        `relative rounded-xl border p-5 shadow-[0_14px_40px_-26px_rgba(0,0,0,0.8)] ${
          isDark
            ? "border-white/10 bg-gradient-to-b from-white/[0.06] to-white/[0.03] text-white"
            : "border-slate-200 bg-white text-slate-900 shadow-sm"
        } ` + (className ?? "")
      }
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div
            className={`text-sm font-semibold tracking-wide ${
              isDark ? "text-white/85" : "text-slate-700"
            }`}
          >
            Market Heatmap
          </div>
          <div
            className={`mt-2 text-xs ${
              isDark ? "text-white/60" : "text-slate-500"
            }`}
          >
            {tab} · {selectedCollectionLabel}
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="mt-4 flex flex-col gap-3">
        <div
          className={
            "flex max-w-full gap-2 border " +
            (isDark ? "border-white/10 bg-white/5" : "border-slate-200 bg-slate-50") +
            " " +
            (tabsExpanded
              ? "items-start rounded-xl p-2 overflow-hidden"
              : "items-center rounded-full p-1")
          }
        >
          <div
            ref={tabsListRef}
            className={
              "flex flex-1 min-w-0 items-center gap-2 px-1 " +
              (tabsExpanded ? "flex-wrap" : "flex-nowrap overflow-hidden")
            }
            role="tablist"
            aria-label="Collections"
          >
            {(tabsExpanded ? collections : displayedCollections).map((c) => {
              const active = c.id === selectedCollectionId;
              return (
                <button
                  key={c.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setSelectedCollectionId(c.id)}
                  className={
                    tabButtonBaseClass +
                    " " +
                    (active
                      ? isDark
                        ? "bg-white/10 text-white"
                        : "bg-white text-slate-900 shadow-sm ring-1 ring-black/5"
                      : isDark
                      ? "text-white/60 hover:text-white"
                      : "text-slate-500 hover:text-slate-900")
                  }
                  title={c.id}
                >
                  {c.label}
                </button>
              );
            })}

            {collections.length === 0 && collectionsLoading ? (
              <div
                className={`px-3 py-1 text-[11px] font-semibold tracking-wide ${
                  isDark ? "text-white/50" : "text-slate-400"
                }`}
              >
                Loading…
              </div>
            ) : null}

            {collections.length === 0 &&
            !collectionsLoading &&
            !collectionsError ? (
              <div
                className={`px-3 py-1 text-[11px] font-semibold tracking-wide ${
                  isDark ? "text-white/50" : "text-slate-400"
                }`}
              >
                No collections
              </div>
            ) : null}
          </div>

          {showTabsToggle ? (
            <button
              type="button"
              aria-label={tabsExpanded ? "Collapse tabs" : "Expand tabs"}
              aria-expanded={tabsExpanded}
              onClick={() => setTabsExpanded((v) => !v)}
              className={
                `shrink-0 rounded-full border transition-colors flex h-6 w-6 items-center justify-center ${
                  isDark
                    ? "border-white/10 bg-white/5 text-white/70 hover:bg-white/10 hover:text-white"
                    : "border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-900"
                }` + (tabsExpanded ? " mt-0.5" : "")
              }
              title={
                tabsExpanded
                  ? "Collapse"
                  : hiddenCount > 0
                  ? `Show all (${hiddenCount} more)`
                  : "Show all"
              }
            >
              <Chevron direction={tabsExpanded ? "up" : "down"} />
            </button>
          ) : null}
        </div>

        {collectionsError ? (
          <div
            className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border px-3 py-2 text-xs ${
              isDark
                ? "border-white/10 bg-white/5 text-white/65"
                : "border-rose-200 bg-rose-50 text-rose-700"
            }`}
          >
            <div className="min-w-0 truncate">
              Failed to load collections: {collectionsError}
            </div>
            <button
              type="button"
              onClick={reloadCollections}
              className={`rounded-lg border px-2 py-1 text-[11px] font-semibold transition-colors ${
                isDark
                  ? "border-white/10 bg-white/5 text-white/80 hover:text-white"
                  : "border-rose-200 bg-white text-rose-700 hover:bg-rose-50"
              }`}
            >
              Retry
            </button>
          </div>
        ) : null}
      </div>

      <div
        ref={tabsMeasureRef}
        className="pointer-events-none absolute left-[-10000px] top-0 opacity-0"
        aria-hidden="true"
      >
        <div className="flex items-center gap-2">
          {collections.map((c) => (
            <div key={c.id} data-col-id={c.id} className={tabButtonBaseClass}>
              {c.label}
            </div>
          ))}
        </div>
      </div>

      {/* Chart */}
      <div
        className={`mt-4 rounded-xl border p-2 ${
          isDark ? "border-white/10 bg-white/5" : "border-slate-200 bg-slate-50"
        }`}
      >
        <div className="relative h-[520px] w-full">
          <div ref={chartDomRef} className="absolute inset-0" />

          {!heatmap && (mapLoading || (autoFetch && collectionsLoading)) ? (
            <div
              className={`absolute inset-0 flex items-center justify-center text-sm ${
                isDark ? "text-white/65" : "text-slate-500"
              }`}
            >
              Loading…
            </div>
          ) : null}

          {mapError ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
              <div
                className={`text-sm ${
                  isDark ? "text-white/75" : "text-slate-700"
                }`}
              >
                Failed to load heatmap
              </div>
              <div
                className={`text-xs ${
                  isDark ? "text-white/55" : "text-slate-500"
                }`}
              >
                {mapError}
              </div>
              <button
                type="button"
                onClick={reloadMap}
                className={`rounded-lg border px-3 py-1.5 text-[11px] font-semibold transition-colors ${
                  isDark
                    ? "border-white/10 bg-white/5 text-white/80 hover:text-white"
                    : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 hover:text-slate-900"
                }`}
              >
                Retry
              </button>
            </div>
          ) : null}

          {!mapError &&
          !mapLoading &&
          !heatmap &&
          !(autoFetch && collectionsLoading) ? (
            <div
              className={`absolute inset-0 flex items-center justify-center text-sm ${
                isDark ? "text-white/65" : "text-slate-500"
              }`}
            >
              No data
            </div>
          ) : null}
        </div>
      </div>

      <div
        className={`mt-3 flex flex-wrap items-center justify-between gap-3 text-xs ${
          isDark ? "text-white/50" : "text-slate-400"
        }`}
      >
        <div className="min-w-0 truncate">&nbsp;</div>
        <div className="flex items-center gap-3">
          <span>Red: down</span>
          <span>Gray: flat</span>
          <span>Green: up</span>
        </div>
      </div>
    </div>
  );
}

export default MarketHeatmapCard;
